import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Dicon } from 'edcom-ui';
import { openSignup } from '../../services/regModal';
import ImageCarousel from '../Carousels/ImageCarousel';
import AccountContext from '../../services/AccountContext';
import { openFeatureWall } from '../../services/payWallModal';
import styles from './RelatedGuidedLesson.module.less';
import clsx from 'clsx';

const summaryItems = [
  {
    key: 'games',
    name: 'games',
    singular: 'game',
    icon: 'game',
  },
  {
    key: 'exercises',
    name: 'online exercises',
    singular: 'online exercise',
    icon: 'exercise',
  },
  {
    key: 'songs',
    name: 'song videos',
    singular: 'song video',
    icon: 'song',
  },
  {
    key: 'stories',
    name: 'interactive stories',
    singular: 'interactive story',
    icon: 'story',
  },
  {
    key: 'worksheets',
    name: 'printable worksheets',
    singular: 'printable worksheet',
    icon: 'worksheet',
  },
];

function RelatedGuidedLesson({
  contentCounts,
  urlName,
  grade,
  subject,
  skill,
  images,
  title,
}) {
  const { ready, memberId, isPremium } = useContext(AccountContext);

  const href = `/guided-lesson/${urlName}/`;

  const summary = summaryItems.map(({ key, name, singular, icon }) => {
    const amount = contentCounts[key];

    if (!amount) {
      return null;
    }

    return (
      <div className={styles.summaryDataItem} key={key}>
        <Dicon name={icon} className={styles.summaryIcon} />
        <span className={styles.summaryData}>
          {`${amount} ${amount > 1 ? name : singular}`}
        </span>
      </div>
    );
  });

  const downloadAllHandler = e => {
    if (!isPremium) {
      e.preventDefault();
    }

    if (ready) {
      if (memberId) {
        openFeatureWall({
          contentType: 'guided-lesson',
          contentId: urlName,
          cid: '10.8551',
          title: 'Unlock Guided Lessons',
          text:
            '<p>Guided Lessons are available to Premium Members only.</p><p>Upgrade to Premium membership to play Guided Lessons, track progress, and cultivate a joy of learning.</p>',
          feature: 'Guided Lesson Download',
        });
      } else {
        openSignup('download', {
          signUp: { title: 'Join Education.com for Free Access to Resources' },
        });
      }
    }
  };

  const startLessonHandler = e => {
    e.preventDefault();

    if (isPremium) {
      window.location.href = href;
      return;
    }

    if (ready) {
      if (memberId) {
        openFeatureWall({
          contentType: 'guided-lesson',
          contentId: urlName,
          cid: '10.8561',
          title: 'Unlock Guided Lessons',
          text:
            '<p>Guided Lessons are available to Premium Members only.</p><p>Upgrade to Premium membership to play Guided Lessons, track progress, and cultivate a joy of learning.</p>',
          feature: 'Guided Lesson',
        });
      } else {
        openSignup('startLesson', {
          signUp: { title: 'Join Education.com for Free Access to Resources' },
        });
      }
    }
  };

  let exerciseInfo = (
    <div className={styles.infoContainer}>
      {summary}
      <div className={styles.downloadPrintables}>
        <a
          onClick={downloadAllHandler}
          href={`/guided-lesson/${urlName}/?__module=LessonDetail&pdf=1`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download all ({contentCounts.worksheets})
        </a>
      </div>
    </div>
  );

  let gradeHtml = grade ? (
    <div className={styles.gradeSection}>
      <div className={styles.dataSubTitle}>Grade</div>
      <div className={styles.gradeText}>{grade.name}</div>
    </div>
  ) : null;

  let subjectHtml = subject ? (
    <>
      <div className={styles.subjectAndSkills}>
        <div className={styles.subjectSection}>
          <span className={styles.dataSubTitle}>Subject</span>
          <span className={styles.subjectText}> {subject.name}</span>
        </div>
      </div>
      {skill ? <div className={styles.skillsSection}>{skill}</div> : ''}
    </>
  ) : null;

  let carouselImages = images.slice(0, 3).map(src => {
    return {
      primaryImage: src,
      url: href,
    };
  });

  return (
    <div className={clsx(styles.container, 'typography-small')}>
      <div className={clsx(styles.titleSection, 'page-width')}>
        <div className={styles.titleBlock}>
          <Dicon name="guided-lesson" theme="dark" />
          <h3 className={styles.title}>Related Guided Lesson</h3>
        </div>
      </div>
      <div className={styles.contentSection}>
        <div className={styles.carouselContainer}>
          <ImageCarousel
            items={carouselImages}
            clickHandler={startLessonHandler}
          />
        </div>
        <div className={styles.lessonData}>
          <div className={styles.lessonContent}>
            <h3 className={styles.lessonTitle}>{title}</h3>
            {exerciseInfo}
            <Button
              size="normal"
              className={styles.button}
              onClick={startLessonHandler}
              href={href}
            >
              View Guided Lesson
            </Button>
            <div className={styles.gradesAndSubjectSection}>
              {gradeHtml}
              {subjectHtml}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RelatedGuidedLesson;

RelatedGuidedLesson.propTypes = {
  title: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  images: PropTypes.array,
  grade: PropTypes.object,
  subject: PropTypes.object,
  skill: PropTypes.string,
  contentCounts: PropTypes.shape({
    games: PropTypes.number,
    songs: PropTypes.number,
    exercises: PropTypes.number,
    stories: PropTypes.number,
    worksheets: PropTypes.number,
  }),
};
